import Service from 'react-reservice';

import { ELoginAs, ELoginMethod, ELoginSource } from '@aqx-core/common/api/auth';
import { ITradingAccount, IUserDetail as IUserDetails } from '@aqx-core/common/api/user-account';
import { ECommonResponseCode } from '@aqx-core/common/request';

import { ILoginFormParams, IViewTradingAccountBalances, IViewTradingAccountDetails } from './types';

export interface IAuthenticationServiceContext {
  loading?: boolean;
  authLoading?: boolean;
  isSwitchingAccount?: boolean;
  authenticated?: boolean;
  selectedTradingAccountDetails?: IViewTradingAccountDetails;
  selectedTradingAccountBalances?: Partial<IViewTradingAccountBalances>;
  loginAs?: ELoginAs;
  lockedUntil?: number;
  loginErrorCode?: ECommonResponseCode;
  source?: ELoginSource;
  prefilledLogin?: ILoginFormParams;
  changePasswordLoading?: boolean;
  tradingAccounts?: ITradingAccount[];
  experienceTokenLoading?: boolean;
  loadingResetPassword: boolean;
  loginMethod: ELoginMethod;
  requestOtpLoading: boolean;
  fetchUrlPasswordLoading: boolean;
  userDetails?: IUserDetails;
}

const defaultContext: IAuthenticationServiceContext = {
  loading: true,
  authLoading: false,
  isSwitchingAccount: false,
  authenticated: undefined,
  selectedTradingAccountBalances: {},
  loginAs: undefined,
  lockedUntil: undefined,
  loginErrorCode: undefined,
  source: undefined,
  prefilledLogin: undefined,
  changePasswordLoading: false,
  tradingAccounts: [],
  experienceTokenLoading: false,
  loadingResetPassword: false,
  loginMethod: ELoginMethod.PASSWORD,
  requestOtpLoading: false,
  fetchUrlPasswordLoading: false
};

class AuthenticationService extends Service<IAuthenticationServiceContext> {
  constructor() {
    super();

    this.context = defaultContext;
  }

  setAuthLoading = (nextAuthLoading: boolean) => {
    this.context.authLoading = nextAuthLoading;
  };

  setLoading = (loading: boolean) => {
    this.context.loading = loading;
  };

  setSwitchingAccount = (value: boolean) => {
    this.context.isSwitchingAccount = value;
  };

  setAuthenticated = (authenticated: boolean) => {
    this.context.authenticated = authenticated;
  };

  setTradingAccountDetails = (nextDetails: IViewTradingAccountDetails) => {
    this.context.selectedTradingAccountDetails = {
      ...nextDetails
    };
  };

  setTradingAccountBalances = (nextBalances: Partial<IViewTradingAccountBalances>) => {
    this.context.selectedTradingAccountBalances = {
      ...this.context.selectedTradingAccountBalances,
      ...nextBalances
    };
  };

  setSource = (nextSource: ELoginSource) => {
    this.context.source = nextSource;
  };

  setLoginAs = (nextLoginAs: ELoginAs) => {
    this.context.loginAs = nextLoginAs;
  };

  setLockedUntil = (nextLockedUntil: number | undefined) => {
    this.context.lockedUntil = nextLockedUntil;
  };

  setLoginErrorCode = (nextLoginErrorCode: ECommonResponseCode | undefined) => {
    this.context.loginErrorCode = nextLoginErrorCode;
  };

  setPrefilledLogin = (details: ILoginFormParams | undefined) => {
    this.context.prefilledLogin = details;
  };

  setChangePasswordLoading = (loading: boolean) => {
    this.context.changePasswordLoading = loading;
  };

  setTradingAccounts = (nextTradingAccounts: ITradingAccount[]) => {
    this.context.tradingAccounts = nextTradingAccounts;
  };

  setExperienceTokenLoading = (loading: boolean) => {
    this.context.experienceTokenLoading = loading;
  };

  setFetchUrlPasswordLoading = (loading: boolean) => {
    this.context.fetchUrlPasswordLoading = loading;
  };

  setUserDetails = (userDetails: IUserDetails | undefined) => {
    this.context.userDetails = userDetails;
  };
}

export const authenticationService = new AuthenticationService();
